import { NotificationType } from "@fitness-app/data-models/entities/Notification";

/**
 * One row per notification type the drawer can filter on.
 *
 * `defaultOn` is the filter the drawer starts with. Menu and preset used to be two hand-written
 * lists and they kept drifting apart — every time they did, the badge counted an unread
 * notification the list then refused to render (TRE-1550, TRE-1390, TRE-1840, TRE-1952). Deriving both from
 * this table means a new type can only be missing from both at once, never from one of them.
 */
export interface NotificationFilterOption {
  type: NotificationType;
  defaultOn: boolean;
}

/** Reaches only PRODUCTS_MANAGE:ALL holders — TRE-1840 routes these by permission, not by role. */
const purchaseNotificationFilters: NotificationFilterOption[] = [
  { type: NotificationType.ClientPurchaseProduct, defaultOn: true },
  { type: NotificationType.ClientPaymentFailed, defaultOn: true },
  { type: NotificationType.ProductPaymentRequiredAction, defaultOn: true },
  { type: NotificationType.ClientRenewedSubscription, defaultOn: true },
  { type: NotificationType.ClientRenewedAccessPeriod, defaultOn: true },
  { type: NotificationType.ClientSubscriptionPlanUpdated, defaultOn: true },
  { type: NotificationType.ClientsImportFinished, defaultOn: true },
  { type: NotificationType.ClientCancelSubscriptionAtPeriodEnd, defaultOn: true },
  { type: NotificationType.ClientResumeSubscriptionAtPeriodEnd, defaultOn: true },
  { type: NotificationType.ClientManualPaymentCreated, defaultOn: true },
  { type: NotificationType.ClientManualPaymentRenewal, defaultOn: true },
  { type: NotificationType.ClientTerminated, defaultOn: true },
];

const generalNotificationFilters: NotificationFilterOption[] = [
  { type: NotificationType.ClientSentVideoRecording, defaultOn: false },
  { type: NotificationType.ClientAddedBodyMeasurements, defaultOn: false },
  { type: NotificationType.ClientAddedWeightMeasurement, defaultOn: false },
  { type: NotificationType.ClientAddedBodyPhotos, defaultOn: false },
  { type: NotificationType.ClientRateWorkout, defaultOn: false },
  { type: NotificationType.ClientRateDish, defaultOn: true },
  { type: NotificationType.ClientReportedIngredientIssue, defaultOn: true },
  { type: NotificationType.ClientReportedDishIssue, defaultOn: true },
  { type: NotificationType.ClientProgramEndingIn1Day, defaultOn: false },
  { type: NotificationType.ClientProgramEndingIn1Week, defaultOn: false },
  { type: NotificationType.ClientSurveyAnswered, defaultOn: false },
  { type: NotificationType.ClientSurveyAnswersUpdated, defaultOn: false },
  { type: NotificationType.SupportRequestAssigned, defaultOn: true },
  { type: NotificationType.SupportCommentMention, defaultOn: true },
  { type: NotificationType.SupportRequestDueSoon, defaultOn: true },
  { type: NotificationType.SupportRequestOverdue, defaultOn: true },
  { type: NotificationType.ChallengeCompletionPending, defaultOn: true },
];

export const getNotificationFilterOptions = (receivesPurchaseNotifications: boolean): NotificationFilterOption[] =>
  receivesPurchaseNotifications
    ? [...purchaseNotificationFilters, ...generalNotificationFilters]
    : generalNotificationFilters;

/**
 * Empty means "no type filter at all". A trainer receives a narrow set of types anyway, so they get
 * everything; an owner would otherwise open the drawer onto years of workout ratings.
 */
export const getDefaultNotificationFilter = (receivesPurchaseNotifications: boolean): NotificationType[] =>
  receivesPurchaseNotifications
    ? getNotificationFilterOptions(true)
        .filter((option) => option.defaultOn)
        .map((option) => option.type)
    : [];
